import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, LogIn } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo ao painel da Clínica COB.");
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cob-beige/60 px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-3xl border border-cob-light bg-white p-9 shadow-[0_30px_70px_-30px_rgba(6,63,50,0.35)]"
      >
        <div className="flex flex-col items-center text-center">
          <LogoMark className="h-14 w-14" />
          <h1 className="mt-5 font-serif text-2xl font-semibold text-cob-dark">Área administrativa</h1>
          <p className="mt-1.5 text-sm text-cob-muted">Acesso restrito à equipe da Clínica COB.</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-semibold uppercase tracking-[0.14em] text-cob-muted">
              E-mail
            </span>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-cob-medium" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                placeholder="seu@email.com"
                className="w-full rounded-xl border border-cob-light bg-white py-3.5 pl-11 pr-4 text-sm text-cob-ink outline-none transition-colors focus:border-cob-green focus:ring-2 focus:ring-cob-green/15"
              />
            </div>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-semibold uppercase tracking-[0.14em] text-cob-muted">
              Senha
            </span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-cob-medium" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                placeholder="••••••••"
                className="w-full rounded-xl border border-cob-light bg-white py-3.5 pl-11 pr-4 text-sm text-cob-ink outline-none transition-colors focus:border-cob-green focus:ring-2 focus:ring-cob-green/15"
              />
            </div>
          </label>

          {error && (
            <p data-testid="admin-login-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-cob-green py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-cob-dark disabled:opacity-60"
          >
            <LogIn className="h-4.5 w-4.5" />
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>

        <a href="/" data-testid="admin-back-to-site" className="mt-6 block text-center text-[13px] font-medium text-cob-muted transition-colors hover:text-cob-green">
          Voltar para o site
        </a>
      </motion.div>
    </div>
  );
}
